import React from 'react'
import { Helmet } from 'react-helmet'

import Header from '../components/header'
import { headerProps } from '../components/HeaderConfig'
import Footer from '../components/footer'
import ThemeToggle from '../components/theme-toggle'
import useTheme from '../hooks/useTheme'
import useScrollAnimation from '../hooks/useScrollAnimation'
import {
  TurnerLogo, DPRLogo, MahindraLogo, VConstructLogo,
  StanfordLogo, IITRoorkeeLogo, NITBhopalLogo
} from '../components/logos'
import './about.css'

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 })
  return (
    <section
      ref={ref}
      className={`about-animated ${isVisible ? 'about-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </section>
  )
}

const experiences = [
  {
    company: 'Turner Construction Company',
    role: 'VDC Assistant Engineer',
    period: 'Apr 2026 - Present',
    location: 'San Francisco Bay Area',
    current: true,
    logo: TurnerLogo,
    description: 'Applying Virtual Design and Construction methodologies to deliver complex building projects through BIM coordination, digital fabrication workflows, and construction technology integration.',
  },
  {
    company: 'DPR Construction',
    period: 'Jun 2023 - Mar 2026',
    logo: DPRLogo,
    roles: [
      {
        role: 'VDC Project Engineer',
        period: 'Jan 2025 - Mar 2026',
        location: 'Santa Clara, CA',
        description: 'Led VDC coordination for $2.5B+ in projects across Life Sciences, Data Centers, Hospitals, and Office developments. Developed AI/generative design platforms for prefabrication. Conducted life cycle assessments for embodied carbon reduction.',
      },
      {
        role: 'Project Engineer',
        period: 'Jun 2024 - Dec 2024',
        location: 'Santa Clara, CA',
        description: 'Managed project engineering workflows for complex commercial and life sciences projects. Coordinated BIM models and construction documentation across multidisciplinary teams.',
      },
      {
        role: 'Project Engineer Intern',
        period: 'Jun 2023 - Sep 2023',
        location: 'Newport Beach, CA',
        description: 'Managed document control and set up Autodesk Construction Cloud for a $200M university research building. Developed embodied carbon reduction strategies through LCA studies.',
      },
    ],
  },
  {
    company: 'Mahindra Lifespace Developers',
    role: 'Sustainability Analyst',
    period: 'Jun 2021 - Jun 2022',
    location: 'Mumbai, India',
    logo: MahindraLogo,
    description: 'Designed Science-Based Targets (SBTi) and Net Carbon Neutrality roadmaps. Led sustainability reporting for GRESB and Net Zero initiatives. Delivered Net Zero Energy and Water Positive building solutions.',
  },
  {
    company: 'vConstruct Pvt. Ltd.',
    role: 'VDC Project Engineer',
    period: 'Jun 2018 - Aug 2019',
    location: 'Pune, India',
    logo: VConstructLogo,
    description: 'Delivered VDC services for 15+ commercial and hospitality projects. Contributed to iOS app development for prefabrication supply chain management.',
  },
]

const education = [
  {
    school: 'Stanford University',
    degree: 'M.S. Civil & Environmental Engineering',
    focus: 'Sustainable Design and Construction',
    period: '2022 - 2024',
    logo: StanfordLogo,
  },
  {
    school: 'IIT Roorkee',
    degree: 'Masters in Architecture',
    focus: 'Architecture',
    period: '2019 - 2021',
    logo: IITRoorkeeLogo,
  },
  {
    school: 'NIT Bhopal',
    degree: 'Bachelors in Architecture',
    focus: 'Architecture',
    period: '2013 - 2018',
    logo: NITBhopalLogo,
  },
]

const skills = [
  { category: 'Languages', items: ['Python', 'JavaScript', 'C#', 'React', 'Node.js', 'HTML/CSS'] },
  { category: 'AI / ML', items: ['TensorFlow', 'PyTorch', 'LLM Fine-tuning', 'RAG', 'NLP', 'Computer Vision'] },
  { category: 'Cloud', items: ['AWS Lambda', 'DynamoDB', 'SageMaker', 'Amplify', 'Azure'] },
  { category: '3D / Web', items: ['Three.js', 'WebGL', 'React Three Fiber', 'WebIFC'] },
  { category: 'BIM / VDC', items: ['Revit', 'Dynamo', 'Navisworks', 'BIM 360', 'Synchro 4D'] },
  { category: 'Sustainability', items: ['OneClick LCA', 'SimaPro', 'Tally', 'DesignBuilder', 'EnergyPlus'] },
]

const awards = [
  { title: 'Y Combinator Top 5%', context: 'Manasara AI', year: '2024' },
  { title: 'TED AI Hackathon', context: 'Top 10 of 150 Teams', year: '2024' },
  { title: 'Autodesk Eduhack', context: 'Winner', year: '2024' },
  { title: 'Stanford Sustainability Challenge', context: 'Winner', year: '2024' },
  { title: 'Best Master\'s Thesis', context: 'IIT Roorkee', year: '2021' },
  { title: 'Solar Decathlon India', context: 'Top 5 Finalist', year: '2020' },
]

const About = () => {
  const { isDark, toggleTheme } = useTheme()

  return (
    <div className="about-container">
      <Helmet>
        <title>About - Abhishek Palit | VDC Assistant Engineer</title>
        <meta property="og:title" content="About - Abhishek Palit" />
        <meta
          name="description"
          content="VDC Assistant Engineer at Turner Construction Company. Stanford graduate specializing in BIM, AI/ML, sustainability, and digital fabrication."
        />
      </Helmet>
      <Header
        {...headerProps}
        rootClassName="headerroot-class-name4"
        themeToggle={<ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />}
      />

      {/* Hero */}
      <div className="about-page">
        <div className="about-hero-section">
          <div className="about-hero-content">
            <span className="about-label">About</span>
            <h1 className="about-hero-title">
              Building the future of<br />
              construction through<br />
              <span className="about-hero-accent">technology & data.</span>
            </h1>
            <p className="about-hero-subtitle">
              I'm an architect and VDC engineer with a passion for advancing the built environment.
              Currently a VDC Assistant Engineer at Turner Construction Company, I combine design thinking with
              computational tools to deliver smarter, more sustainable infrastructure.
            </p>
          </div>
          <div className="about-hero-stats">
            <div className="about-stat">
              <span className="about-stat-number">$2.5B+</span>
              <span className="about-stat-label">Project Value</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-number">15+</span>
              <span className="about-stat-label">Projects Delivered</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-number">6</span>
              <span className="about-stat-label">Awards Won</span>
            </div>
          </div>
        </div>

        {/* Experience */}
        <AnimatedSection className="about-experience-section">
          <h2 className="about-section-title">Experience</h2>
          <div className="about-timeline">
            {experiences.map((exp, i) => (
              <div key={i} className={`about-timeline-item ${exp.current ? 'about-current' : ''}`}>
                <div className="about-timeline-marker">
                  <div className="about-timeline-dot" />
                  {i < experiences.length - 1 && <div className="about-timeline-line" />}
                </div>
                <div className="about-timeline-content">
                  {exp.roles ? (
                    <>
                      <div className="about-timeline-header">
                        <div className="about-timeline-info">
                          {exp.logo && <exp.logo className="about-company-logo" />}
                          <div>
                            <h3 className="about-timeline-company">{exp.company}</h3>
                            <p className="about-timeline-role about-timeline-tenure">{exp.period}</p>
                          </div>
                        </div>
                      </div>
                      <div className="about-grouped-roles">
                        {exp.roles.map((r, j) => (
                          <div key={j} className={`about-grouped-role ${j < exp.roles.length - 1 ? 'about-grouped-role-border' : ''}`}>
                            <div className="about-grouped-role-header">
                              <p className="about-grouped-role-title">{r.role}</p>
                              <div className="about-timeline-meta">
                                <span className="about-timeline-period">{r.period}</span>
                                <span className="about-timeline-location">{r.location}</span>
                              </div>
                            </div>
                            <p className="about-timeline-desc">{r.description}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="about-timeline-header">
                        <div className="about-timeline-info">
                          {exp.logo && <exp.logo className="about-company-logo" />}
                          <div>
                            <h3 className="about-timeline-company">{exp.company}</h3>
                            <p className="about-timeline-role">{exp.role}</p>
                          </div>
                        </div>
                        <div className="about-timeline-meta">
                          <span className="about-timeline-period">{exp.period}</span>
                          <span className="about-timeline-location">{exp.location}</span>
                        </div>
                      </div>
                      <p className="about-timeline-desc">{exp.description}</p>
                      {exp.current && <span className="about-current-badge">Current</span>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Education */}
        <AnimatedSection className="about-education-section">
          <h2 className="about-section-title">Education</h2>
          <div className="about-edu-grid">
            {education.map((edu, i) => (
              <div key={i} className="about-edu-card">
                <div className="about-edu-header">
                  {edu.logo && <edu.logo className="about-edu-logo" />}
                  <span className="about-edu-period">{edu.period}</span>
                </div>
                <h3 className="about-edu-school">{edu.school}</h3>
                <p className="about-edu-degree">{edu.degree}</p>
                <p className="about-edu-focus">{edu.focus}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Skills */}
        <AnimatedSection className="about-skills-section">
          <h2 className="about-section-title">Technical Expertise</h2>
          <div className="about-skills-grid">
            {skills.map((group, i) => (
              <div key={i} className="about-skill-group">
                <h3 className="about-skill-category">{group.category}</h3>
                <div className="about-skill-tags">
                  {group.items.map((item, j) => (
                    <span key={j} className="about-skill-tag">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Awards */}
        <AnimatedSection className="about-awards-section">
          <h2 className="about-section-title">Recognition</h2>
          <div className="about-awards-grid">
            {awards.map((award, i) => (
              <div key={i} className="about-award-card">
                <span className="about-award-year">{award.year}</span>
                <h3 className="about-award-title">{award.title}</h3>
                <p className="about-award-context">{award.context}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Teaching */}
        <AnimatedSection className="about-teaching-section">
          <h2 className="about-section-title">Teaching & Leadership</h2>
          <div className="about-teaching-grid">
            <div className="about-teaching-card">
              <StanfordLogo className="about-teaching-logo" />
              <h3>Owner & Mentor</h3>
              <p className="about-teaching-course">CEE 222A — Computer Integrated AEC Global Teamwork</p>
              <p className="about-teaching-detail">Stanford University, Jan 2024 - Present</p>
              <p className="about-teaching-desc">Guiding interdisciplinary teams on BIM workflows, sustainability strategies, and collaborative project delivery.</p>
            </div>
            <div className="about-teaching-card">
              <StanfordLogo className="about-teaching-logo" />
              <h3>Teaching Assistant</h3>
              <p className="about-teaching-course">CEE 327 — Construction Robotics</p>
              <p className="about-teaching-detail">Stanford University, Apr - Jul 2024</p>
              <p className="about-teaching-desc">Facilitated student learning in robotics technologies for construction automation.</p>
            </div>
            <div className="about-teaching-card">
              <StanfordLogo className="about-teaching-logo" />
              <h3>Course Assistant</h3>
              <p className="about-teaching-course">CEE 222A — Computer Integrated AEC Global Teamwork</p>
              <p className="about-teaching-detail">Stanford University, Jan - Apr 2024</p>
              <p className="about-teaching-desc">Supported international cohort with technical guidance on BIM tools and coordinated team-based project deliverables.</p>
            </div>
            <div className="about-teaching-card">
              <IITRoorkeeLogo className="about-teaching-logo" />
              <h3>Teaching Assistant</h3>
              <p className="about-teaching-course">Computer Applications in AEC</p>
              <p className="about-teaching-detail">IIT Roorkee, Jun - Dec 2020</p>
              <p className="about-teaching-desc">Taught computational tools and digital methodologies for architecture, engineering, and construction workflows.</p>
            </div>
          </div>
        </AnimatedSection>

        {/* Contact CTA — same style as home page */}
        <AnimatedSection className="about-contact-section">
          <div className="about-contact-card-dark">
            <div className="about-contact-dark-content">
              <span className="about-contact-dark-label">Get in touch</span>
              <h2 className="about-contact-dark-heading">
                Let's connect.
              </h2>
              <p className="about-contact-dark-text">
                I'm always interested in discussing construction technology, sustainability, and the future of the built environment.
              </p>
              <div className="about-contact-dark-links">
                <a href="mailto:palit@stanford.edu" className="about-contact-dark-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 4l-10 8L2 4" />
                  </svg>
                  palit@stanford.edu
                </a>
                <a href="https://www.linkedin.com/in/abhishek-palit/" target="_blank" rel="noopener noreferrer" className="about-contact-dark-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
                <a href="https://github.com/abhipalit3" target="_blank" rel="noopener noreferrer" className="about-contact-dark-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                  </svg>
                  GitHub
                </a>
              </div>
              <p className="about-contact-dark-location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                San Francisco Bay Area, CA
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>

      <Footer rootClassName="footerroot-class-name6"></Footer>
    </div>
  )
}

export default About
